import {
  BaseMetadata,
  CompilationManifest,
  DesignModel,
  MicroserviceCollection,
  Sitemap,
  SitemapReferenceItem,
  VersionedDesignModel,
} from '@msdk/definitions'

import { ApiClient } from '../core'
import { ApiError } from '../core/errors'

export type BaseMetadataAPI = {
  getBaseMetadata(): Promise<BaseMetadata>
  getModel(msProviderName: string): Promise<DesignModel>
  getVersionedModel(msProviderName: string, version: string): Promise<VersionedDesignModel | undefined>
  getSiteMap(name: string): Promise<Sitemap>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getCompilationManifest(): Promise<any>
  getMicroserviceList(): Promise<MicroserviceCollection>
  resetMetadata(): Promise<void>
  getSitemapItems(): Promise<SitemapReferenceItem[]>
}

export function initializeMetadataAPI(c: ApiClient): BaseMetadataAPI {
  c.setRefToken()

  /**
   * The function `getBaseMetadata` is an asynchronous function that retrieves base metadata from an API
   * and returns it as a Promise.
   * @returns The function `getBaseMetadata` is returning a Promise that resolves to an object of type
   * `BaseMetadata`.
   */
  const getBaseMetadata = async (): Promise<BaseMetadata> => {
    const res = await c.read<BaseMetadata>(`CMPS/api/composer/basemetadata`)
    if (res instanceof ApiError) {
      throw new Error(`💥 ${JSON.stringify(res)}`)
    } else {
      return res
    }
  }

  /**
   * The function `getModel` retrieves a design model from a metadata API endpoint and returns it as a
   * Promise.
   * @param {string} msProviderName - The `msProviderName` parameter is a string that represents the name
   * of the microservice provider. It is used to construct the API endpoint URL for retrieving the
   * metadata model.
   * @returns The function `getModel` returns a Promise that resolves to a `DesignModel` object.
   */
  const getModel = async (msProviderName: string): Promise<DesignModel> => {
    const res = await c.read<DesignModel>(`${msProviderName}/api/metadata/model`)
    if (res instanceof ApiError) {
      throw new Error(`💥 ${JSON.stringify(res)}`)
    } else {
      return res
    }
  }

  const getVersionedModel = async (
    msProviderName: string,
    version: string,
  ): Promise<VersionedDesignModel | undefined> => {
    const res = await c.readWithHeaders<DesignModel>(
      `${msProviderName}/api/metadata/versionedmodel`,
      undefined,
      undefined,
      {
        'If-None-Match': version,
      },
    )
    if (res instanceof ApiError) {
      // 304 means no new model
      if (res.status === 304) {
        return undefined
      }
      throw new Error(`💥 ${JSON.stringify(res)}`)
    } else {
      return { version: res.headers['etag'], model: res.data }
    }
  }

  /**
   * The function `getSiteMap` is an asynchronous function that retrieves a sitemap by name and returns a
   * Promise that resolves to a Sitemap object.
   * @param {string} name - The `name` parameter is a string that represents the name of the sitemap. It
   * is used to fetch the sitemap data from the API.
   * @returns The function `getSiteMap` returns a Promise that resolves to an instance of the `Sitemap`
   * class.
   */
  const getSiteMap = async (name: string): Promise<Sitemap> => {
    // Commenting following JWT login due to deprecation in favor of Ref token
    // c.setJwtToken()
    const res = await c.read<Sitemap>(`CMPS/api/composer/sitemap/${name}`)
    if (res instanceof ApiError) {
      throw new Error(`💥 ${JSON.stringify(res)}`)
    } else {
      return new Sitemap(res)
    }
  }

  const getSitemapItems = async (): Promise<SitemapReferenceItem[]> => {
    // Commenting following JWT login due to deprecation in favor of Ref token
    // c.setJwtToken()
    const res = await c.read<SitemapReferenceItem[]>('CMPS/api/composer/sitemapitems')
    if (res instanceof ApiError) {
      throw new Error(`💥 ${JSON.stringify(res)}`)
    } else {
      return res
    }
  }

  /**
   * The function `getCompilationManifest` retrieves a compilation manifest from an API endpoint and
   * returns it as a Promise.
   * @returns The function `getCompilationManifest` returns a Promise that resolves to a
   * `CompilationManifest` object.
   */
  const getCompilationManifest = async (): Promise<CompilationManifest> => {
    // Commenting following JWT login due to deprecation in favor of Ref token
    // c.setJwtToken()
    const res = await c.read<CompilationManifest>(`CMPS/api/composer/manifest`)
    if (res instanceof ApiError) {
      throw new Error(`💥 ${JSON.stringify(res)}`)
    } else {
      return res
    }
  }

  /**
   * The function `getMicroserviceList` retrieves the microservice list from an API endpoint and
   * returns it as a Promise.
   * @returns The function `getMicroserviceList` returns a Promise that resolves to a
   * `MicroserviceCollection` object.
   */
  const getMicroserviceList = async (): Promise<MicroserviceCollection> => {
    const res = await c.read<MicroserviceCollection>('CMPS/api/Composer/microservicesInfo')
    if (res instanceof ApiError) {
      throw new Error(`💥 ${JSON.stringify(res)}`)
    } else {
      return res
    }
  }

  const resetMetadata = async () => {
    const res = await c.read<void>('CMPS/api/Composer/resetMetadata')
    if (res instanceof ApiError) {
      throw new Error(`💥 ${JSON.stringify(res)}`)
    } else {
      return res
    }
  }

  return {
    getBaseMetadata,
    getModel,
    getVersionedModel,
    getSiteMap,
    getCompilationManifest,
    getMicroserviceList,
    resetMetadata,
    getSitemapItems,
  }
}
